import React, {useEffect} from "react";
import {
    Box,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography, useTheme
} from "@mui/material";
import {useRace} from "../../../utils/races/RaceContext";
import {useForm} from "react-hook-form";
import {Race, raceSchema} from "../../../schemas/races/racesSchema";
import {zodResolver} from "@hookform/resolvers/zod";
import Loading from "../../../components/status/Loading";
import {Add, Remove} from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useNotification} from "../../../utils/hooks/useNotification";
import {useCategories} from "../../../utils/categories/CategoriesContext";

const RulesSettings: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {showNotification} = useNotification();
    const {race, updateRace, isLoading} = useRace();
    const {categories, isLoading: isLoadingCategories} = useCategories();
    const {
        register,
        setValue,
        formState: {errors, dirtyFields},
        reset,
        watch,
    } = useForm<Race>({
        resolver: zodResolver(raceSchema),
    });

    useEffect(() => {
        reset(race);
    }, [race, reset])

    const handleStartNumbersChange = async (value: boolean) => {
        setValue("rules.startNumbers", value);
        await updateRace(watch());
    }

    const handleTotalLapsCountForOverallResultsChange = async () => {
        if (!dirtyFields.rules?.totalLapsCountForOverallResults) return;

        const value = watch("rules.totalLapsCountForOverallResults");

        if (isNaN(value) || value < 1) {
            setValue("rules.totalLapsCountForOverallResults", 1);
            return;
        }

        // if value is bigger than some category laps count, show notification error and fix values, otherwise update race
        const categoryLapsCount = watch("rules.categoryLapsCount");
        const isValid = Object.values(categoryLapsCount).every((count) => count >= value);
        if (!isValid) {
            showNotification("Počet kol pro celkové pořadí nesmí být větší než pro jednotlivé kategorie.", "info");
            setValue("rules.categoryLapsCount", {
                ...categoryLapsCount,
                ...Object.fromEntries(
                    Object.entries(categoryLapsCount).map(([key, count]) => [key, Math.max(count, value)])
                )
            });
        }

        await updateRace(watch());
    }

    const handleCategoryLapsCountChange = async (categoryId: number) => {
        if (!dirtyFields.rules?.categoryLapsCount) return;

        const value = watch(`rules.categoryLapsCount`)?.[categoryId];

        if (isNaN(value) || value < 1) {
            setValue(`rules.categoryLapsCount`, {
                ...watch(`rules.categoryLapsCount`),
                [categoryId]: 1
            });
            return;
        }

        // if category leap count is smaller than total leaps count, display notification and set category leap count to total leaps count
        const totalLapsCount = watch("rules.totalLapsCountForOverallResults");
        if (value < totalLapsCount) {
            showNotification("Počet kol pro jednotlivé kategorie nesmí být menší než pro celkové pořadí.", "info");
            setValue(`rules.categoryLapsCount`, {
                ...watch(`rules.categoryLapsCount`),
                [categoryId]: totalLapsCount
            });
        } else {
            setValue(`rules.categoryLapsCount`, {
                ...watch(`rules.categoryLapsCount`),
                [categoryId]: value
            });
        }

        await updateRace(watch());
    }

    if (isLoading || !race || isLoadingCategories || !categories) return <Loading/>;

    return (
        <Stack direction={{xs: "column", sm: "row"}} spacing={{xs: 5, sm: 10}}
               sx={{width: '100%', maxWidth: "100%", px: {xs: 4, sm: 0}, boxSizing: 'border-box'}}>
            <Stack direction="column" spacing={5}>
                <Stack direction="column" spacing={2}>
                    <Typography fontWeight="bold">
                        Počet kol pro započítání do celkového pořadí
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton onClick={async () => {
                            setValue("rules.totalLapsCountForOverallResults", watch("rules.totalLapsCountForOverallResults") - 1, {
                                shouldDirty: true,
                            });
                            await handleTotalLapsCountForOverallResultsChange();
                        }}
                                    size="small">
                            <Remove/>
                        </IconButton>
                        <TextField
                            variant="standard"
                            required
                            type={isMobile ? "text" : "number"}
                            inputMode="numeric"
                            error={!!errors.rules?.totalLapsCountForOverallResults}
                            helperText={errors.rules?.totalLapsCountForOverallResults?.message}
                            inputProps={{min: 1, max: 100}}
                            sx={{
                                width: isMobile ? "60%" : "80%",
                            }}
                            value={watch("rules.totalLapsCountForOverallResults") ?? 0}
                            onChange={(e) => {
                                const value = parseInt(e.target.value);
                                setValue("rules.totalLapsCountForOverallResults", isNaN(value) ? 0 : value, {
                                    shouldDirty: true,
                                });
                            }}
                            onBlur={async () => await handleTotalLapsCountForOverallResultsChange()}
                        />
                        <IconButton onClick={async () => {
                            setValue("rules.totalLapsCountForOverallResults", watch("rules.totalLapsCountForOverallResults") + 1, {
                                shouldDirty: true,
                            });
                            await handleTotalLapsCountForOverallResultsChange();
                        }}
                                    size="small">
                            <Add/>
                        </IconButton>
                    </Box>
                </Stack>
                <Stack direction="column" spacing={2}>
                    <Typography fontWeight="bold">
                        Počet kol pro jednotlivé kategorie
                    </Typography>
                    {categories.map((category) =>
                        <Stack direction="row" key={category.id} alignItems="baseline">
                            <Typography sx={{minWidth: 160}}>
                                {category.name}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconButton
                                    onClick={async () => {
                                        setValue(`rules.categoryLapsCount`, {
                                            ...watch(`rules.categoryLapsCount`),
                                            [category.id]: watch(`rules.categoryLapsCount`)?.[category.id] - 1
                                        }, {
                                            shouldDirty: true,
                                        });
                                        await handleCategoryLapsCountChange(category.id);
                                    }}
                                    size="small">
                                    <Remove/>
                                </IconButton>
                                <TextField
                                    key={category.id}
                                    variant="standard"
                                    required
                                    type={isMobile ? "text" : "number"}
                                    inputMode="numeric"
                                    error={!!errors.rules?.categoryLapsCount?.[category.id]}
                                    helperText={errors.rules?.categoryLapsCount?.[category.id]?.message}
                                    inputProps={{min: 1, max: 100}}
                                    sx={{minWidth: 15}}
                                    value={watch(`rules.categoryLapsCount`)?.[category.id] ?? 0}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        setValue(`rules.categoryLapsCount`, {
                                            ...watch(`rules.categoryLapsCount`),
                                            [category.id]: isNaN(value) ? 0 : value
                                        }, {
                                            shouldDirty: true,
                                        });
                                    }}
                                    onBlur={async () => await handleCategoryLapsCountChange(category.id)}
                                />
                                <IconButton
                                    onClick={async () => {
                                        setValue(`rules.categoryLapsCount`, {
                                            ...watch(`rules.categoryLapsCount`),
                                            [category.id]: watch(`rules.categoryLapsCount`)?.[category.id] + 1
                                        }, {
                                            shouldDirty: true,
                                        });
                                        await handleCategoryLapsCountChange(category.id);
                                    }}
                                    size="small">
                                    <Add/>
                                </IconButton>
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </Stack>
            <Stack direction="column" spacing={2}>
                <Typography fontWeight="bold">
                    Použití startovních čísel
                </Typography>
                <FormControl component="fieldset">
                    <RadioGroup
                        {...register("rules.startNumbers")}
                        value={watch("rules.startNumbers") ?? false}
                        sx={{px: 2}}
                        onChange={(e) => handleStartNumbersChange(e.target.value === "true")}
                    >
                        <FormControlLabel
                            value={true}
                            control={<Radio/>}
                            label="Ano"
                        />
                        <FormControlLabel
                            value={false}
                            control={<Radio/>}
                            label="Ne"
                        />
                    </RadioGroup>
                </FormControl>
            </Stack>
        </Stack>
    );
}

export default RulesSettings;